import React, { createContext, useCallback, useContext, useState } from "react";
import { 
  getSuppliersPaginated, 
  getSuppliers, 
  getSupplierById, 
  createSupplier, 
  updateSupplier, 
  deleteSupplier, 
  SupplierApiResponse
} from "../../services/supplierService";
import { getErrorMessage } from "../../locales/translationUtils";
import LoadingOverlay from "../../components/Loading";
import Supplier from "../../types/supplier";
import Paginacao from "../../types/paginacao";
import { ApiResponse } from "../../services/api";

interface SupplierContextProps {
  apiResponse: ApiResponse<Supplier> | null;
  suppliers: Supplier[] | null;
  supplierById: Supplier | null;
  loading: boolean;
  error: string | null;
  fetchSuppliers: (paginacao: Paginacao | null) => void;
  fetchSupplierById: (id: number) => Promise<Supplier | null>;
  postSupplier: (supplier: Supplier) => Promise<SupplierApiResponse | null>;
  putSupplier: (id: number, supplier: Supplier) => Promise<SupplierApiResponse | null>;
  excluiSupplier: (id: number) => Promise<boolean>;
}

interface SupplierProviderProps {
  children: React.ReactNode;
}

const SupplierContext = createContext<SupplierContextProps | null>(null);

const SupplierProvider: React.FC<SupplierProviderProps> = ({ children }) => {
  const [apiResponse, setApiResponse] = useState<ApiResponse<Supplier> | null>(null);
  const [suppliers, setSupplier] = useState<Supplier[] | null>(null);
  const [supplierById, setSupplierById] = useState<Supplier | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSuppliers = useCallback(async (paginacao: Paginacao | null) => {
    setError(null);
    try {
      setLoading(true);
      const response = (paginacao) ? await getSuppliersPaginated(paginacao) : await getSuppliers();
      setApiResponse(response);
      setSupplier(response.data.values || []);
    } catch (err) {
      setSupplier([]);
      setError(getErrorMessage("fetchSupplierError"));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSupplierById = useCallback(async (id: number) => {
    setError(null);
    try {
      setLoading(true);
      const response = await getSupplierById(id);
      
      setSupplierById(response.data);
      return response.data;
    } catch (err) {
      setError(getErrorMessage("fetchSupplierByIdError"));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const postSupplier = useCallback(async (supplier: Supplier) => {
    setError(null);
    try {
      setLoading(true);
      const response = await createSupplier(supplier);
      return response;
    } catch (err) {
      setError(getErrorMessage("createSupplierError"));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const putSupplier = useCallback(async (id: number, supplier: Supplier) => {
    setError(null);
    try {
      setLoading(true);
      const response = await updateSupplier(id, supplier);
      return response;
    } catch (err) {
      setError(getErrorMessage("updateSupplierError"));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const excluiSupplier = useCallback(async (id: number) => {
    setError(null);
    try {
      setLoading(true);
      await deleteSupplier(id);
      return true;
    } catch (err) {
      setError(getErrorMessage("deleteSupplierError"));
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <SupplierContext.Provider 
      value={{ 
        apiResponse,
        suppliers, 
        supplierById, 
        loading, 
        error, 
        fetchSuppliers, 
        fetchSupplierById, 
        postSupplier, 
        putSupplier, 
        excluiSupplier 
      }}
    >
      <LoadingOverlay isLoading={loading} />
      {children}
    </SupplierContext.Provider>
  );
};

export default SupplierProvider;

export const useSupplier = (): SupplierContextProps => {
  const context = useContext(SupplierContext);
  if (!context) {
    throw new Error(
      getErrorMessage("wrongUseProviderError", {
        auth: useSupplier?.name,
        providerName: SupplierProvider?.name,
      })
    );
  }
  return context;
};
